import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { fetchEvaluation } from '@modules/performance/action/fetch-evaluation';
import { EvaluationDetailResponse } from '@modules/performance/types';
import { useEvaluationStore } from './store';

interface DrawerStore {
  drawerEvaluation: EvaluationDetailResponse | null;
  setDrawerEvaluation: Dispatch<
    SetStateAction<EvaluationDetailResponse | null>
  >;
  handleFetchDrawerEvaluation: (evaluationId: string) => Promise<any>;
  drawerLoading: boolean;
  currentEvaluationId: string | null;
  setCurrentEvaluationId: (id: string | null) => void;
  initializeDrawer: (evaluationId: string) => Promise<void>;
  onCloseDrawer?: () => void;
  setOnCloseDrawer: (callback: (() => void) | undefined) => void;
}

const DrawerContext = createContext<DrawerStore | null>(null);

export const DrawerStoreProvider = ({ children }: PropsWithChildren<{}>) => {
  const organisation = useAppSelector(selected);
  const { evaluation } = useEvaluationStore();
  const [drawerEvaluation, setDrawerEvaluation] =
    useState<EvaluationDetailResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentEvaluationId, setCurrentEvaluationId] =
    useState<string | null>(null);
  const [onCloseDrawer, setOnCloseDrawer] =
    useState<(() => void) | undefined>(undefined);

  const handleFetchDrawerEvaluation = useCallback(
    async (evaluationId: string) => {
      setDrawerLoading(true);
      try {
        if (evaluation && evaluation.data?.id === evaluationId) {
          setDrawerEvaluation(evaluation);
          setCurrentEvaluationId(evaluationId);
          setDrawerLoading(false);
          return evaluation;
        }

        const response = await fetchEvaluation(organisation.id, evaluationId);
        setDrawerEvaluation(response as any);
        setCurrentEvaluationId(evaluationId);
        return response;
      } catch (error) {
        console.error('Error fetching drawer evaluation:', error);
        throw error;
      } finally {
        setDrawerLoading(false);
      }
    },
    [organisation.id, evaluation]
  );

  const initializeDrawer = useCallback(
    async (evaluationId: string) => {
      if (evaluation && evaluation.data?.id === evaluationId) {
        setDrawerEvaluation(evaluation);
        setCurrentEvaluationId(evaluationId);
        return;
      }

      if (!drawerEvaluation || drawerEvaluation.data?.id !== evaluationId) {
        await handleFetchDrawerEvaluation(evaluationId);
      }
    },
    [drawerEvaluation, handleFetchDrawerEvaluation, evaluation]
  );

  const value = useMemo(
    () => ({
      drawerEvaluation,
      setDrawerEvaluation,
      handleFetchDrawerEvaluation,
      drawerLoading,
      currentEvaluationId,
      setCurrentEvaluationId,
      initializeDrawer,
      onCloseDrawer,
      setOnCloseDrawer,
    }),
    [
      drawerEvaluation,
      setDrawerEvaluation,
      handleFetchDrawerEvaluation,
      drawerLoading,
      currentEvaluationId,
      setCurrentEvaluationId,
      initializeDrawer,
      onCloseDrawer,
      setOnCloseDrawer,
    ]
  );

  return (
    <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
  );
};

export const useDrawerStore = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawerStore must be used within a DrawerStoreProvider');
  }
  return context;
};
